import { clamp } from "lodash";
import { websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import {
  IWebsiteBuilderContainerBase,
  IWebsiteBuilderContainerDataBase,
} from "./websiteBuilderContainer";
import { CSSProperties } from "react";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import {
  websiteBuilderGenerateDefaultHideableNodeVisibility,
} from "../websiteBuilderHideableNode";
import { IWebsiteBuilderGridCell, websiteBuilderGenerateDefaultGridCell } from "../gridCell/websiteBuilderGridCell";
import classNames from "classnames";
import { WB_GRID_COLUMNS_CSS_VARIABLE, WB_GRID_ROWS_CSS_VARIABLE } from "../../website/shared/cssVariables";

export const WEBSITE_BUILDER_GRID_MIN_ROWS = 1;
export const WEBSITE_BUILDER_GRID_MAX_ROWS = 25;

export const WEBSITE_BUILDER_GRID_MIN_COLUMNS = 1;
export const WEBSITE_BUILDER_GRID_MAX_COLUMNS = 25;

export const WEBSITE_BUILDER_GRID_MIN_GAP = 16;
export const WEBSITE_BUILDER_GRID_MAX_GAP = 100;

export type TWebsiteBuilderGridCellAnchor =
  | "top-left"
  | "top"
  | "top-right"
  | "left"
  | "center"
  | "right"
  | "bottom-left"
  | "bottom"
  | "bottom-right";

function isWebsiteBuilderGridCellAnchor(value: string): value is TWebsiteBuilderGridCellAnchor {
  return (
    value === "top-left" ||
    value === "top" ||
    value === "top-right" ||
    value === "left" ||
    value === "center" ||
    value === "right" ||
    value === "bottom-left" ||
    value === "bottom" ||
    value === "bottom-right"
  );
}

export interface IWebsiteBuilderGridSettings {
  rows: number;
  columns: number;
  gap: number;
  cellAnchor: TWebsiteBuilderGridCellAnchor;
  cellFill: boolean;
}

interface IWebsiteBuilderGridData extends IWebsiteBuilderContainerDataBase {
  type: "grid";
  settings: IWebsiteBuilderGridSettings;
}

export interface IWebsiteBuilderGrid extends IWebsiteBuilderContainerBase<IWebsiteBuilderGridCell[]> {
  container: IWebsiteBuilderGridData;
}

export function isWebsiteBuilderGrid(node: any): node is IWebsiteBuilderGrid {
  return node?.type === "container" && node.container?.type === "grid";
}

function generateDefaultGridSettings(): IWebsiteBuilderGridSettings {
  return {
    rows: 2,
    columns: 2,
    gap: WEBSITE_BUILDER_GRID_MIN_GAP,
    cellAnchor: "top-left",
    cellFill: false,
  };
}

export function websiteBuilderGenerateDefaultGrid(): IWebsiteBuilderGrid {
  const settings = generateDefaultGridSettings();

  return {
    id: crypto.randomUUID(),
    type: "container",
    container: {
      type: "grid",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 400, height: 400 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 300, height: 300 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: 300 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings,
    },
    children: Array.from({ length: settings.rows * settings.columns }, websiteBuilderGenerateDefaultGridCell),
  };
}

export function websiteBuilderNormalizeGridSettings(
  partialSettings: Partial<IWebsiteBuilderGridSettings>,
): IWebsiteBuilderGridSettings {
  const defaultSettings = generateDefaultGridSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { rows, columns, gap, cellAnchor, cellFill } = partialSettings;

  const normalizedGridSettings: IWebsiteBuilderGridSettings = {
    rows: websiteBuilderNormalizeValue({
      type: "number",
      value: rows,
      defaultValue: defaultSettings.rows,
      required: true,
      transform: (v: number) => clamp(Math.floor(v), WEBSITE_BUILDER_GRID_MIN_ROWS, WEBSITE_BUILDER_GRID_MAX_ROWS),
    }),
    columns: websiteBuilderNormalizeValue({
      type: "number",
      value: columns,
      defaultValue: defaultSettings.columns,
      required: true,
      transform: (v: number) => clamp(Math.floor(v), WEBSITE_BUILDER_GRID_MIN_COLUMNS, WEBSITE_BUILDER_GRID_MAX_COLUMNS),
    }),
    gap: websiteBuilderNormalizeValue({
      type: "number",
      value: gap,
      defaultValue: defaultSettings.gap,
      required: true,
      transform: (v: number) => clamp(v, WEBSITE_BUILDER_GRID_MIN_GAP, WEBSITE_BUILDER_GRID_MAX_GAP),
    }),
    cellAnchor: websiteBuilderNormalizeValue({
      type: "string",
      value: cellAnchor,
      defaultValue: defaultSettings.cellAnchor,
      required: true,
      validate: isWebsiteBuilderGridCellAnchor,
    }) as TWebsiteBuilderGridCellAnchor,
    cellFill: websiteBuilderNormalizeValue({
      type: "boolean",
      value: cellFill,
      defaultValue: defaultSettings.cellFill,
      required: true,
    }),
  };

  return normalizedGridSettings;
}

interface IWebsiteBuilderGridDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
}

export function websiteBuilderGenerateGridDesignStyles(
  settings: IWebsiteBuilderGridSettings,
): IWebsiteBuilderGridDesignStyles {
  const styleProperties: CSSProperties = {
    [WB_GRID_ROWS_CSS_VARIABLE]: settings.rows,
    [WB_GRID_COLUMNS_CSS_VARIABLE]: settings.columns,
    gap: `${settings.gap}px`,
  };

  let styleDeclaration = "";

  styleDeclaration += `${WB_GRID_ROWS_CSS_VARIABLE}: ${settings.rows};`;
  styleDeclaration += `${WB_GRID_COLUMNS_CSS_VARIABLE}: ${settings.columns};`;
  styleDeclaration += `gap: ${styleProperties.gap};`;

  return {
    containerClassName: "website-builder-grid-container",
    className: classNames(`website-builder-grid grid-cell-anchor-${settings.cellAnchor}`, {
      "grid-cell-fill": settings.cellFill,
    }),
    styleProperties,
    styleDeclaration,
  };
}
